import { Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from 'environments/environment';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { User } from './core/user/user.types';
import { UserService } from './core/user/user.service';
import { AuthUtils } from './core/auth/auth.utils';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgxSpinnerModule } from 'ngx-spinner';
import { appVersion } from '../environments/version';
import { InfoService } from './core/backend-infos/info.services';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { Meta } from '@angular/platform-browser';
import { CentralNotificationsComponent } from './modules/shared/components/central-notifications/central-notifications.component';
import { UtmService } from './core/utm/utm.service';
import { UrlUtils } from './modules/shared/utils/url-utils';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        RouterOutlet,
        NgxSpinnerModule,
        TourMatMenuModule,
        CentralNotificationsComponent
    ],
})
export class AppComponent implements OnInit, OnDestroy {
    // Private
    private _unsubscribeAll: Subject<any>;
    isLoggedIn = false;
    currentUser: User;

    // App versions
    backendVersion: string;
    frontendVersion: string = appVersion;

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _userService: UserService,
        private _infoService: InfoService,
        private _metaService: Meta,
        private _utmService: UtmService,
        private _router: Router
    ) {
    }

    /**
     * Crisp chat widget should never appear on backoffice routes (/admin/*).
     * Two layers of defence:
     *   1. Push 'chat:hide' / 'chat:show' to the Crisp queue (works once the
     *      Crisp script has booted).
     *   2. Toggle a `crisp-suppressed` class on <body>, picked up by a global
     *      CSS rule that hard-hides `.crisp-client`. This catches the case
     *      where Crisp loads after our route check fires.
     */
    private toggleCrispForRoute(url: string): void {
        const hide = url.startsWith('/admin');
        document.body.classList.toggle('crisp-suppressed', hide);
        const crisp = (window as { $crisp?: unknown[] }).$crisp;
        if (crisp && Array.isArray(crisp)) {
            crisp.push(['do', hide ? 'chat:hide' : 'chat:show']);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the private defaults
        this._unsubscribeAll = new Subject();

        // Capture UTM parameters from the current URL (redundant fallback for index.html script)
        // This must run BEFORE any authentication or routing logic to ensure UTMs are captured
        this._utmService.captureUtmParams();

        // Hide Crisp on /admin/* (backoffice), show it elsewhere.
        this.toggleCrispForRoute(this._router.url);
        this._router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event) => {
                this.toggleCrispForRoute(event.urlAfterRedirects);
                // The trackers wait for an address without secret (e-mail links): a navigation, such as the
                // redirect after the e-mail validation, may have brought one
                UrlUtils.loadTrackersOnCleanAddress();
            });

        this._infoService.getInfos()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(data => {
                this.backendVersion = data.version;
                console.log(`Frontend version: ${this.frontendVersion}`);
                console.log(`Backend version: ${this.backendVersion}`);
            });

        // Deactivate themeOptionsPanel for production environment
        if (environment.production) {
            // this.themeOptionsPanelVisible = false; // TODO Cacher setting panel de droite 
        }

        // Add "noindex, nofollow" meta tag when not in production to prevent indexing by search engines
        if (!environment.production) {
            this._metaService.updateTag({ name: 'robots', content: 'noindex, nofollow' });
        }

        this.isLoggedIn = !!this._authService.accessToken;
        if (this.isLoggedIn) {
            if (!this._authService.check()) {
                this._authService.signOut();
            } else {
                const userToken = AuthUtils.getUserToken();

                let user = AuthUtils.buildUserFromUserToken(userToken);

                this._userService.user = user;
            }
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

}
